import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { regulatoryApi } from '@api/regulatoryApi';
import type {
  RegulatoryDocumentFilters,
  CreateRegulatoryDocumentRequest,
  UpdateRegulatoryDocumentRequest,
  RecallFilters,
  CreateRecallRequest,
  VigilanceFilters,
  CreateVigilanceRequest,
  UpdateVigilanceRequest,
  SubmitVigilanceRequest,
  CloseVigilanceRequest,
} from '@/types/regulatory';

// ── Documents ─────────────────────────────────────────────

export function useRegulatoryDocuments(mdFilters: RegulatoryDocumentFilters) {
  return useQuery({
    queryKey: ['regulatory', 'documents', mdFilters],
    queryFn: () => regulatoryApi.getDocuments(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useCreateRegulatoryDocument() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateRegulatoryDocumentRequest) =>
      regulatoryApi.createDocument(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'documents'] });
      message.success('Документ створено');
    },
    onError: () => {
      message.error('Помилка при створенні документа');
    },
  });
}

export function useUpdateRegulatoryDocument() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateRegulatoryDocumentRequest) =>
      regulatoryApi.updateDocument(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'documents'] });
      message.success('Документ оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні документа');
    },
  });
}

export function useDeleteRegulatoryDocument() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => regulatoryApi.deleteDocument(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'documents'] });
      message.success('Документ видалено');
    },
    onError: () => {
      message.error('Помилка при видаленні документа');
    },
  });
}

// ── Recalls ───────────────────────────────────────────────

export function useRecalls(mdFilters: RecallFilters) {
  return useQuery({
    queryKey: ['regulatory', 'recalls', mdFilters],
    queryFn: () => regulatoryApi.getRecalls(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useRecallById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['regulatory', 'recalls', mdId],
    queryFn: () => regulatoryApi.getRecallById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreateRecall() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateRecallRequest) =>
      regulatoryApi.createRecall(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'recalls'] });
      message.success('Recall створено');
    },
    onError: () => {
      message.error('Помилка при створенні recall');
    },
  });
}

export function useActivateRecall() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => regulatoryApi.activateRecall(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'recalls'] });
      message.success('Recall активовано');
    },
    onError: () => {
      message.error('Помилка при активації recall');
    },
  });
}

export function useCompleteRecall() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => regulatoryApi.completeRecall(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'recalls'] });
      message.success('Recall завершено');
    },
    onError: () => {
      message.error('Помилка при завершенні recall');
    },
  });
}

export function useAffectedCustomers(mdRecallId: string | undefined) {
  return useQuery({
    queryKey: ['regulatory', 'recalls', mdRecallId, 'affected-customers'],
    queryFn: () => regulatoryApi.getAffectedCustomers(mdRecallId!),
    enabled: !!mdRecallId,
  });
}

// ── Vigilance ─────────────────────────────────────────────

export function useVigilanceReports(mdFilters: VigilanceFilters) {
  return useQuery({
    queryKey: ['regulatory', 'vigilance', mdFilters],
    queryFn: () => regulatoryApi.getVigilanceReports(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useVigilanceById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['regulatory', 'vigilance', mdId],
    queryFn: () => regulatoryApi.getVigilanceById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreateVigilance() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateVigilanceRequest) =>
      regulatoryApi.createVigilance(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'vigilance'] });
      message.success('Звіт vigilance створено');
    },
    onError: () => {
      message.error('Помилка при створенні звіту');
    },
  });
}

export function useUpdateVigilance() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateVigilanceRequest) =>
      regulatoryApi.updateVigilance(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'vigilance'] });
      message.success('Звіт vigilance оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні звіту');
    },
  });
}

export function useSubmitVigilance() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { id: string } & SubmitVigilanceRequest) =>
      regulatoryApi.submitVigilance(mdParams.id, { dsmdReportNumber: mdParams.dsmdReportNumber }),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'vigilance'] });
      message.success('Звіт подано до ДСМД');
    },
    onError: () => {
      message.error('Помилка при поданні звіту');
    },
  });
}

export function useStartVigilanceInvestigation() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => regulatoryApi.startVigilanceInvestigation(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'vigilance'] });
      message.success('Розслідування розпочато');
    },
    onError: () => {
      message.error('Помилка при початку розслідування');
    },
  });
}

export function useCloseVigilance() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { id: string } & CloseVigilanceRequest) =>
      regulatoryApi.closeVigilance(mdParams.id, {
        investigation: mdParams.investigation,
        correctiveAction: mdParams.correctiveAction,
      }),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['regulatory', 'vigilance'] });
      message.success('Звіт vigilance закрито');
    },
    onError: () => {
      message.error('Помилка при закритті звіту');
    },
  });
}
